import React, { PureComponent } from 'react';
import { ReactEditor } from 'slate-react';
import { Transforms } from 'slate';

import { createTableCell, createTableRow } from '../../utils/doc-editor';

import DocNodeWithChooser from '../doc-node-with-chooser';

import IconRemove from '../../icons/icon-remove';

const DEFAULT_COL_SIZES = [.33, .33, .33];

const equals = (a, b) => {
    if (!a || !b) {
        return false;
    }

    return a.toString() === b.toString();
}

const normalizeColumnSizes = sizes => {
    const sum = sizes.reduce((a, b) => a + b, 0);

    return sizes.map(size => size / sum);
}

const RowControl = ({ index, onClick, onRowAdd, onRowRemove }) => {
    const handleAddRowClick = () => {
        if (onRowAdd) {
            onRowAdd(index);
        }
    }

    const handleRemoveRowClick = () => {
        if (onRowRemove) {
            onRowRemove(index);
        }
    }

    return (
        <div className="table-control" contentEditable={false}>
            <span className="control-holder control-remove-holder" contentEditable={false}>
                <button contentEditable={false} className="btn btn-secondary btn-circle btn-xxsmall control control-remove" onClick={handleRemoveRowClick}><IconRemove /></button>
            </span>

            <span className="control-holder control-add-holder" contentEditable={false}>
                <button contentEditable={false} className="btn btn-secondary btn-colored btn-circle btn-xxsmall control control-add" onClick={handleAddRowClick}>+</button>
            </span>

            <span className="highlight" contentEditable={false} />
        </div>
    )
}

const ColControl = ({ index, isLast, onClick, onColAdd, onColRemove, onResize }) => {
    let element = null;

    const handleAddColClick = () => {
        if (onColAdd) {
            onColAdd(index);
        }
    }

    const handleRemoveColClick = () => {
        if (onColRemove) {
            onColRemove(index);
        }
    }

    const handleResizeStart = event => {
        const tableWidth = element.parentNode.clientWidth;
        const cellWidth = element.clientWidth;
        const startX = event.clientX;
        const handleMouseUp = event => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            handleResize(event.clientX, true);
        };

        const handleMouseMove = event => {
            handleResize(event.clientX);
        }

        const handleResize = (newX, save) => {
            if (onResize) {
                onResize(index, (cellWidth + (newX - startX)) / tableWidth, save);
            }
        }

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }

    return (
        <div ref={el => element = el} className="table-control" contentEditable={false}>
            <span className="control-holder control-remove-holder" contentEditable={false}>
                <button contentEditable={false} className="btn btn-secondary btn-circle btn-xxsmall control control-remove" onClick={handleRemoveColClick}><IconRemove /></button>
            </span>

            <span className="control-holder control-add-holder" contentEditable={false}>
                <button contentEditable={false} className="btn btn-secondary btn-colored btn-circle btn-xxsmall control control-add" onClick={handleAddColClick}>+</button>
            </span>

            <span className="highlight" contentEditable={false} />

            {
                !isLast ? <span className="resize-handle" contentEditable={false} onMouseDown={handleResizeStart} /> : null
            }

        </div>
    )
}

export default class DocTable extends PureComponent {
    state = {
        columnsCount: 0,
        columnSizes: this.props.element.data && this.props.element.data.columnSizes ? this.props.element.data.columnSizes : DEFAULT_COL_SIZES
    }

    handleMenuAction = type => {
        this.props.onNodeChange && this.props.onNodeChange(this.props.element, type);
    }

    handleRowRemoveClick = index => {
        const { element, onRemove} = this.props;

        if (this.rows.length === 1) {
            this.removeTable();
            return;
        }

        onRemove(element.children[index]);
    }

    handleColRemoveClick = index => {
        const { element, onRemove } = this.props;

        if (this.state.columnsCount === 1) {
            this.removeTable();
            return;
        }

        const columnSizes = [...this.state.columnSizes];
        columnSizes.splice(index, 1);

        this.setState({
            columnSizes: normalizeColumnSizes(columnSizes),
            columnsCount: this.state.columnsCount - 1
        });


        for (var i = 0; i < this.rows.length; i++) {
            onRemove(element.children[i].children[index]);
        }

        this.saveColumnSizes(normalizeColumnSizes(columnSizes));
    }

    handleRowAddClick = index => {
        const { element, editor } = this.props;
        let last = false;

        if (typeof index !== 'number') {
            index = this.rows.length - 1;
            last = true;
        }

        let path = ReactEditor.findPath(editor, element.children[index]);
        const row = createTableRow(this.state.columnsCount);

        if (last) {
            // Move to the last one
            path = [...path.slice(0, -1), path[path.length - 1] + 1];
        }

        Transforms.insertNodes(editor, row, {
            at: path,
            mode: 'all'
        });

        this.props.onRowAdd && this.props.onRowAdd(index);
    }

    handleColAddClick = index => {
        const { element, editor } = this.props;
        let last = false;

        if (this.state.columnsCount >= 33) {
            return;
        }

        if (typeof index !== 'number') {
            index = this.state.columnsCount - 1;
            last = true;
        }

        const columnSizes = [...this.state.columnSizes];
        columnSizes.splice(index, 0, 1 / this.state.columnsCount);

        this.setState({
            columnSizes: normalizeColumnSizes(columnSizes),
            columnsCount: this.state.columnsCount + 1
        });

        element.children.forEach(row => {
            if (!row.children) {
                return;
            }

            let path = ReactEditor.findPath(editor, row.children[index]);

            if (last) {
                // Move to the last one
                path = [...path.slice(0, -1), path[path.length - 1] + 1];
            }

            Transforms.insertNodes(editor, createTableCell(), {
                at: path
            });
        });

        this.saveColumnSizes(normalizeColumnSizes(columnSizes));
    }

    handleColResize = (index, newSize, save) => {
        const columnSizes = [...this.state.columnSizes];
        const oldSize = columnSizes[index];

        columnSizes[index] = newSize;
        columnSizes[index + 1] += oldSize - newSize;

        this.setState({ columnSizes });

        if (save) {
            this.saveColumnSizes(columnSizes);
        }
    }

    handleRemoveTableClick = () => {
        this.removeTable();
    }

    removeTable() {
        const { editor, element, onRemove} = this.props;
        this.focus();
        Transforms.move(editor, {
            distance: 1,
            unit: 'line',
            reverse: true
        });

        setTimeout(() => {
            onRemove(element);
        });
    }

    saveColumnSizes(columnSizes) {
        const { element, onNodeUpdate } = this.props;

        onNodeUpdate && onNodeUpdate(element, element.type, { columnSizes });
    }

    focus() {
        const { editor, element } = this.props;
        const at = ReactEditor.findPath(editor, element);

        Transforms.setSelection(editor, {
            anchor: { path: at, offset: 0 },
            focus: { path: at, offset: 0 }
        });
    }

    resizeRowsAndCols(oldColumnSizes) {
        if (this.tableBody) {
            const rows = this.rows;
            const cols = rows.length ? Array.from(rows[0].children) : [];
            const rowControls = this.tableRowControls ? Array.from(this.tableRowControls.querySelectorAll('.table-control')) : [];
            const colControls = this.tableRowControls ? Array.from(this.tableColControls.querySelectorAll('.table-control')) : [];
            const columnSizes = this.props.element.data && this.props.element.data.columnSizes ? this.props.element.data.columnSizes : DEFAULT_COL_SIZES;

            if (this.state.columnsCount !== cols.length) {
                this.setState({
                    columnsCount: cols.length
                });
            }

            if (columnSizes && !equals(columnSizes, oldColumnSizes) && !equals(columnSizes, this.state.columnSizes)) {
                this.setState({ columnSizes });
            }

            rows.forEach((row, index) => {
                if (rowControls[index]) {
                    rowControls[index].style.height = `${row.clientHeight + (index === 0 ? 1 : 0)}px`;
                }
            });

            cols.forEach((col, index) => {
                if (colControls[index]) {
                    colControls[index].style.width = `${col.clientWidth + (index === 0 ? 2 : 1)}px`;
                }
            });
        }
    }

    get rows() {
        return Array.from(this.tableBody.children);
    }

    componentDidUpdate(prevProps) {
        this.resizeRowsAndCols(prevProps.element.data && prevProps.element.data.columnSizes);
    }

    componentDidMount() {
        this.resizeRowsAndCols(this.state.columnsCount);
    }

    render() {
        const { attributes, children, element, readOnly } = this.props;
        const cols = new Array(this.state.columnsCount).fill(null);

        const columnSizes = this.state.columnSizes || [];

        return (
            <DocNodeWithChooser current={element.type} element={attributes.ref.current} items={this.props.menuItems} depth={1} onAction={this.handleMenuAction}>
                <div className="doc-node doc-table" {...attributes}>
                    <div className="doc-table-scroller">
                        <div className="doc-table-inner">
                            <table>
                                <colgroup contentEditable={false}>
                                    {
                                        columnSizes.map((size, index) => <col key={index} width={`${size * 100}%`} contentEditable={false} />)
                                    }
                                </colgroup>

                                <tbody ref={el => this.tableBody = el}>
                                    { children }
                                </tbody>
                            </table>
                            {
                                !readOnly ? (
                                    <React.Fragment>
                                        <div className="doc-table-controls doc-table-row-controls" contentEditable={false} ref={el => this.tableRowControls = el}>
                                            {
                                                element.children.map((row, index) => <RowControl key={index} index={index} onRowRemove={this.handleRowRemoveClick} onRowAdd={this.handleRowAddClick} />)
                                            }

                                            <span className="control-holder control-add-holder" contentEditable={false}>
                                                <button key="last" contentEditable={false} className="btn btn-secondary btn-colored btn-circle btn-xxsmall control control-add" onClick={this.handleRowAddClick}>+</button>
                                            </span>

                                            <span className="control-holder control-remove-holder" contentEditable={false}>
                                                <button key="last-remove" contentEditable={false} className="btn btn-secondary btn-circle btn-xxsmall control control-remove" onClick={this.handleRemoveTableClick}><IconRemove /></button>
                                            </span>

                                            <span className="highlight" />
                                        </div>

                                        <div className="doc-table-controls doc-table-col-controls" contentEditable={false} ref={el => this.tableColControls = el}>
                                            {
                                                cols.map((col, index) => <ColControl key={index} index={index} isLast={index === cols.length - 1} onColRemove={this.handleColRemoveClick} onColAdd={this.handleColAddClick} onResize={this.handleColResize} />)
                                            }

                                            <span className="control-holder control-add-holder" contentEditable={false}>
                                                <button key="last" contentEditable={false} className="btn btn-secondary btn-colored btn-circle btn-xxsmall control control-add" onClick={this.handleColAddClick}>+</button>
                                            </span>

                                            <span className="highlight" contentEditable={false} />
                                        </div>
                                    </React.Fragment>
                                ) : null
                            }
                        </div>
                    </div>
                </div>
            </DocNodeWithChooser>
        )
    }
}
